/*
 * Google tag (GA4) in Consent Mode v2: the tag loads on every page with storage denied and
 * is switched to granted per category the player accepts in the cookie banner. Without
 * REACT_APP_GA_MEASUREMENT_ID nothing loads. Injected here, not inline in index.html,
 * because the CSP allows no inline scripts and the id is a build-time env key.
 */
import { type CookieConsent, onCookieConsentChange, readCookieConsent } from "./cookieConsent";

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

const MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID ?? "";

const consentState = (consent: CookieConsent | null) => {
  const analytics = consent?.analytics ? "granted" : "denied";
  const marketing = consent?.marketing ? "granted" : "denied";
  return {
    analytics_storage: analytics,
    ad_storage: marketing,
    ad_user_data: marketing,
    ad_personalization: marketing,
  };
};

// Reset, verify and unsubscribe links carry secrets in the query; UTM tags must stay for attribution.
const SECRET_PARAM = /token|code|key|hash|sig|email/i;

export const safeLocation = (): string => {
  const url = new URL(window.location.href);
  for (const name of [...url.searchParams.keys()]) {
    if (SECRET_PARAM.test(name)) url.searchParams.delete(name);
  }
  url.hash = "";
  return url.toString();
};

export const initGtag = (): void => {
  if (!MEASUREMENT_ID || window.gtag) return;

  window.dataLayer = window.dataLayer ?? [];
  window.gtag = function gtag() {
    // gtag.js reads the arguments object itself, not an array copy of it.
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments);
  };

  // The default must be queued before config, or the first hit goes out before consent is known.
  window.gtag("consent", "default", { ...consentState(null), wait_for_update: 500 });
  window.gtag("consent", "update", consentState(readCookieConsent()));
  window.gtag("set", { page_location: safeLocation() });
  window.gtag("js", new Date());
  // Page views come from the router (App.tsx, trackPageView), with the section as content group.
  window.gtag("config", MEASUREMENT_ID, { send_page_view: false });

  onCookieConsentChange((consent) => window.gtag?.("consent", "update", consentState(consent)));

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(MEASUREMENT_ID)}`;
  document.head.appendChild(script);
};
